import React, { useCallback, useMemo, useRef } from 'react';

import PropTypes from 'prop-types';
import PaletteProductCardCompact from 'web-palette/dist/components/ProductCardCompact/ProductCardCompact';

import Button from '@ulta/core/components/Button/Button';
import Image from '@ulta/core/components/Image/Image';
import Link_Huge from '@ulta/core/components/Link_Huge/Link_Huge';
import { useIntersectionProcessor } from '@ulta/core/hooks/useIntersectionProcessor/useIntersectionProcessor';
import { useDeviceInflection } from '@ulta/core/providers/InflectionProvider/InflectionProvider';
import { useOverlay } from '@ulta/core/providers/OverlayProvider/OverlayProvider';
import { spreadProps } from '@ulta/core/utils/dom/dom';
import { handleRedirect } from '@ulta/core/utils/handleLocation/handleLocation';

import * as utils from './ProductCardCompact';

/**
  * Represents an ProductCardCompact component
  * @method
  * @param {ProductCardCompactProps} props - React properties passed from composition
  * @returns PalProductCardCompact
  */
const ProductCardCompact = ( props ) => {
  const { breakpoint } = useDeviceInflection();
  const { isOverlay } = useOverlay();
  const PalProductCardCompact = PaletteProductCardCompact.default || PaletteProductCardCompact;
  const productCardCompactRef = useRef( null );
  const {
    skuId,
    productId,
    addToBagAction,
    invokeMutation,
    viewOptionAction
  } = props;

  const atbAction = {
    ...addToBagAction,
    variables: {
      moduleParams: {
        skuId,
        productId
      }
    }
  };

  const isAddOverlay = isOverlay( atbAction.navigationType );
  if( !isAddOverlay ){
    atbAction.variables.moduleParams.quantity = 1;
  }

  /**
   * Method to handle action callback for atbAction
  */
  const handleOnClick = useCallback(
    utils.composeOnClick(
      { productId, skuId, atbAction, isAddOverlay },
      { invokeMutation }
    ),
    [invokeMutation, productId, skuId, isAddOverlay, atbAction]
  );

  const formattedFooterActionElement = (
    <>
      { !viewOptionAction && addToBagAction && (
        <Button
          ariaHiddenIcon={ true }
          tiny={ true }
          onClick={ handleOnClick }
          action={ atbAction }
          label={ atbAction.label }
        />
      ) }
      { viewOptionAction && (
        <Button
          ariaHiddenIcon={ true }
          tiny={ true }
          secondary={ true }
          onClick={ ( e ) => {
            e.stopPropagation();
            e.preventDefault();
            handleRedirect( { url: viewOptionAction.url } );
          } }
          action={ viewOptionAction }
          label={ viewOptionAction.label }
        />
      ) }
    </>
  );

  const productCardCompactProps = useMemo( utils.composeProductCardCompactProps(
    { props, breakpoint },
    { formattedFooterActionElement } ),
  [props, breakpoint] );

  useIntersectionProcessor( {
    ...props,
    componentRef: productCardCompactRef
  } );

  return (
    <div
      className='ProductCardCompact'
      ref={ productCardCompactRef }
    >
      <PalProductCardCompact
        { ...spreadProps( { ...productCardCompactProps } ) }
      />
    </div>
  );
};
/**
 * Helper function to format media and actions based on provided data and methods.
 *
 * @param {Object} props - The incoming properties to be formatted for rendering.
 * @param {Object} props.breakpoint - The breakpoint object to determine device type, allowing conditional formatting.
 * @param {string} [props.image] - The desktop image source URL.
 * @param {Object} [props.action] - Action containing external action; used to format actions if available.
 * @returns {Object} An object containing all original `props`, with additional formatted media and action properties:
 * - `externalMediaElement` (formatted media, or `null` if no media is provided)
 * - `externalActionElement` (formatted action, or `null` if no actions are available).
 */
export const composeProductCardCompactProps = ( data, methods ) => () => {
  const { breakpoint, props } = data || {};
  const { formattedFooterActionElement } = methods || {};

  if( !breakpoint || !props ){
    return {};
  }

  const {
    image,
    action,
    ...productCardCompactProps
  } = props;

  // Objects for external media and actions coming from Amplience
  let formattedMedia = null;
  let formattedAction = null;
  // Formatters for incoming media and actions
  if( image ){
    formattedMedia = (
      <Image
        alt={ image.altText }
        src={ image.imageUrl }
        ariaHidden={ true }
        shouldUseIntersectionObserver={ false }
      />
    );
  }
  if( action ){
    formattedAction = (
      <Link_Huge
        action={ action }
        fullyClickableParent
      ></Link_Huge>
    );
  }
  return {
    ...productCardCompactProps,
    externalMediaElement: formattedMedia,
    externalActionElement: formattedAction,
    externalFooterActionElement: formattedFooterActionElement
  };
};

/**
   * Method to handle invoking add to bag from product card actions
   * @param {object} methods methods
   * @param {action} methods.invokeMutation handler from layerhost to invoke DXL mutation
   * @param {string} action DXL action
   */
export const composeOnClick = ( data, methods ) => () => {
  const { skuId, productId, atbAction, isAddOverlay } = data || {};
  const { invokeMutation } = methods || {};
  const { graphql, customHeaders, params, variables } = atbAction || {};
  if( !invokeMutation || !skuId || !productId || !atbAction || isAddOverlay ){
    return;
  }
  const mutation = {
    graphql,
    customHeaders,
    params,
    variables
  };
  invokeMutation( mutation );
};

/**
 * Property type definitions
 * @typedef ProductCardCompactProps
 * @type {object}
 * @property {string} additionalOffersText - second valueTag text
 * @property {string} ariaLabelBookmark - aria-label for the bookmark
 * @property {string} badge - top left badge text
 * @property {boolean} displayBookmark - sets the flag to display bookmark
 * @property {object} externalActionElement - fully clickable card action from web-site
 * @property {object} externalBookmarkAction - bookmark action coming from web-site
 * @property {object} externalFooterActionElement - addToBagAction coming from web-site
 * @property {object} externalMediaElement - the externalMediaElement for the ProductCardDefault
 * @property {boolean} fullyClickableParent - boolean if card can be fully clickable
 * @property {string} brandName - brandName for the product card
 * @property {string} kitPrice - kit price of the product
 * @property {string} kitPriceAccessibilityLabel - Sets the kit price accessibility label
 * @property {string} listPrice - list price of the product
 * @property {string} listPriceAccessibilityLabel - Sets the list price accessibility label
 * @property {string} productName - productName for the product card
 * @property {string} promoText - promo text for the product
 * @property {number} rating - rating of the product
 * @property {string} reviewAccessibilityLabel - the reviewAccessibilityLabel for the product
 * @property {number} reviewCount - the reviewCount for the product
 * @property {string} salePrice - sale price of the product
 * @property {string} salePriceAccessibilityLabel - Sets the sale price accessibility label
 * @property {object} screenReaderText - screenReaderText
 * @property {string} variantLabel - support text of the product card
 * @property {object} actionGroups - product action (palette action)
 * @property {object} addToBagAction - addToBagAction (palette action)
 * @property {object} bookmarkAction - action object for the bookmark (palette action)
 * @property {string} priceLabel - price label of the product
 * @property {object} removeFavoriteAction - sets the remove Favorite Action (palette action)
 * @property {string} sponsoredBadgeLabel - sponsored text of the product card
 */
export const propTypes = {
  additionalOffersText: PropTypes.string,
  ariaLabelBookmark: PropTypes.string,
  badge: PropTypes.string,
  displayBookmark: PropTypes.bool,
  externalActionElement: PropTypes.object,
  externalBookmarkAction: PropTypes.object,
  externalFooterActionElement: PropTypes.object,
  externalMediaElement: PropTypes.object,
  fullyClickableParent: PropTypes.bool,
  brandName: PropTypes.string,
  kitPrice: PropTypes.string,
  kitPriceAccessibilityLabel: PropTypes.string,
  listPrice: PropTypes.string,
  listPriceAccessibilityLabel: PropTypes.string,
  productName: PropTypes.string,
  promoText: PropTypes.string,
  rating: PropTypes.number,
  reviewAccessibilityLabel: PropTypes.string,
  reviewCount: PropTypes.number,
  salePrice: PropTypes.string,
  salePriceAccessibilityLabel: PropTypes.string,
  screenReaderText: PropTypes.object,
  variantLabel: PropTypes.string,
  actionGroups: PropTypes.object,
  addToBagAction: PropTypes.object,
  bookmarkAction: PropTypes.object,
  priceLabel: PropTypes.string,
  removeFavoriteAction: PropTypes.object,
  sponsoredBadgeLabel: PropTypes.string
};
export default ProductCardCompact;